<!-- ReactivateMemberModal.vue - Updated to use cbhiNumber -->
<template>
  <div class="modal fade" id="reactivateMemberModal" tabindex="-1" role="dialog"
       aria-labelledby="reactivateMemberModalLabel" aria-hidden="true"
       data-bs-backdrop="static" data-bs-keyboard="false">
    <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
      <div class="modal-content">
        <div class="modal-header bg-success">
          <h5 class="modal-title text-white" id="reactivateMemberModalLabel">
            <i class="bx bx-play-circle me-2"></i>
            Reactivate Member
          </h5>
          <button type="button" class="btn-close btn-close-white" @click="closeModal"
                  aria-label="Close" :disabled="processing"></button>
        </div>

        <div class="modal-body">
          <!-- Error Alert Display -->
          <div v-if="errorMessage" class="alert alert-danger alert-dismissible fade show mb-4" role="alert">
            <div class="d-flex align-items-start">
              <i class="bx bx-error-circle fs-4 me-2"></i>
              <div class="flex-grow-1">
                <strong>Error:</strong> {{ errorMessage }}
              </div>
              <button type="button" class="btn-close" @click="clearError" aria-label="Close"></button>
            </div>
          </div>

          <!-- Success Alert Display (optional) -->
          <div v-if="successMessage" class="alert alert-success alert-dismissible fade show mb-4" role="alert">
            <div class="d-flex align-items-start">
              <i class="bx bx-check-circle fs-4 me-2"></i>
              <div class="flex-grow-1">
                {{ successMessage }}
              </div>
              <button type="button" class="btn-close" @click="clearSuccess" aria-label="Close"></button>
            </div>
          </div>

          <!-- Member Information Summary -->
          <div class="alert alert-success mb-4">
            <div class="d-flex align-items-start">
              <i class="bx bx-check-circle fs-3 me-3"></i>
              <div class="flex-grow-1">
                <h6 class="alert-heading mb-2">
                  Reactivating Suspended Member:
                  <strong>{{ memberFullName }}</strong>
                </h6>
                <p class="mb-2">
                  <strong>CBHI Number:</strong> {{ cbhiNumber }}<br>
                  <strong>Current Status:</strong>
                  <span class="badge bg-warning">{{ capitalizeFirst(memberInfo.status) }}</span><br>
                  <strong>Suspended on:</strong> {{ formatDate(memberInfo.suspension_date || memberInfo.suspensionDate) }}<br>
                  <strong>Suspension Category:</strong> {{ formatSuspensionCategory(memberInfo.suspension_category || memberInfo.suspensionCategory) }}
                </p>
                <hr class="my-2">
                <p class="mb-0 small">
                  <i class="bx bx-info-circle me-1"></i>
                  <strong>Reactivation will:</strong>
                </p>
                <ul class="mb-0 small">
                  <li>Restore member status to Active</li>
                  <li>Reactivate all {{ beneficiaryCount }} suspended beneficiaries</li>
                  <li>Resume premium collection</li>
                  <li>Allow new claims to be submitted</li>
                  <li>Resume any held claims for processing</li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Reactivation Options -->
          <div class="mb-4">
            <h6 class="fw-bold mb-3">
              <i class="bx bx-cog me-1"></i>
              Reactivation Options
            </h6>

            <div class="form-check mb-2">
              <input class="form-check-input" type="checkbox" id="reactivateBeneficiaries"
                     v-model="reactivateBeneficiaries">
              <label class="form-check-label" for="reactivateBeneficiaries">
                Reactivate all suspended beneficiaries ({{ beneficiaryCount }} total)
              </label>
            </div>

            <div class="form-check mb-2">
              <input class="form-check-input" type="checkbox" id="resumePendingClaims"
                     v-model="resumePendingClaims">
              <label class="form-check-label" for="resumePendingClaims">
                Resume processing of held claims
              </label>
            </div>

            <div class="form-check mb-2">
              <input class="form-check-input" type="checkbox" id="notifyMemberReactivation"
                     v-model="notifyMember">
              <label class="form-check-label" for="notifyMemberReactivation">
                Send notification to member about reactivation
              </label>
            </div>
          </div>

          <!-- Reactivation Reason -->
          <div class="mb-4">
            <label for="reactivationReason" class="form-label fw-bold">
              <i class="bx bx-edit me-1"></i>
              Reactivation Reason <span class="text-danger">*</span>
            </label>
            <textarea class="form-control" id="reactivationReason" rows="3"
                      v-model="reactivationReason"
                      @input="updateCharCount"
                      placeholder="Please provide the reason for reactivating this member. Include any relevant circumstances or conditions that have been met."
                      :class="{ 'is-invalid': showValidation && !isReasonValid }"
                      required></textarea>
            <div class="d-flex justify-content-between mt-1">
              <small class="text-muted">
                Minimum {{ minReasonLength }} characters required
              </small>
              <small :class="charCountClass">
                {{ charCount }} / {{ minReasonLength }}
              </small>
            </div>
            <div v-if="showValidation && !isReasonValid" class="invalid-feedback d-block">
              Please provide a detailed reason (minimum {{ minReasonLength }} characters)
            </div>
          </div>

          <!-- Conditions Met Checklist -->
          <div class="mb-4">
            <h6 class="fw-bold mb-3">
              <i class="bx bx-check-square me-1"></i>
              Confirm Conditions Met
            </h6>
            <div class="card bg-light">
              <div class="card-body">
                <div class="form-check mb-2">
                  <input class="form-check-input" type="checkbox" id="conditionPayment"
                         v-model="conditions.paymentResolved">
                  <label class="form-check-label" for="conditionPayment">
                    Any outstanding payment issues have been resolved
                  </label>
                </div>
                <div class="form-check mb-2">
                  <input class="form-check-input" type="checkbox" id="conditionDocumentation"
                         v-model="conditions.documentationComplete">
                  <label class="form-check-label" for="conditionDocumentation">
                    Required documentation has been submitted and verified
                  </label>
                </div>
                <div class="form-check mb-2">
                  <input class="form-check-input" type="checkbox" id="conditionEligibility"
                         v-model="conditions.eligibilityConfirmed">
                  <label class="form-check-label" for="conditionEligibility">
                    Member eligibility has been confirmed
                  </label>
                </div>
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" id="conditionApproval"
                         v-model="conditions.supervisorApproval">
                  <label class="form-check-label" for="conditionApproval">
                    Supervisor approval obtained (if required)
                  </label>
                </div>
              </div>
            </div>
          </div>

          <!-- Admin Authorization -->
          <div class="card bg-light mb-4">
            <div class="card-body">
              <h6 class="card-title mb-3">
                <i class="bx bx-shield-security me-1"></i>
                Authorization Required
              </h6>
              <div class="mb-3">
                <label for="adminPasswordReactivate" class="form-label">
                  Enter your password to confirm <span class="text-danger">*</span>
                </label>
                <input type="password" class="form-control" id="adminPasswordReactivate"
                       v-model="adminPassword"
                       placeholder="Enter your password"
                       :class="{ 'is-invalid': showValidation && !adminPassword }">
                <div v-if="showValidation && !adminPassword" class="invalid-feedback">
                  Password is required for authorization
                </div>
              </div>
            </div>
          </div>

          <!-- Final Confirmation -->
          <div class="form-check">
            <input class="form-check-input" type="checkbox" id="confirmReactivation"
                   v-model="confirmReactivation">
            <label class="form-check-label text-success fw-bold" for="confirmReactivation">
              I confirm that all conditions have been met and authorize the reactivation of this member
            </label>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal"
                  :disabled="processing">
            <i class="bx bx-x me-1"></i> Cancel
          </button>
          <button type="button" class="btn btn-success" @click="validateAndSubmit"
                  :disabled="processing || !confirmReactivation || !allConditionsMet">
            <span v-if="!processing">
              <i class="bx bx-play-circle me-1"></i>
              Reactivate Member
            </span>
            <span v-else>
              <span class="spinner-border spinner-border-sm me-1" role="status"></span>
              Processing...
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";

export default {
  name: "ReactivateMemberModal",
  props: {
    // Changed from memberId to cbhiNumber as primary identifier
    cbhiNumber: {
      type: String,
      required: true
    },
    memberInfo: {
      type: Object,
      required: true
    },
    beneficiaryCount: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      reactivationReason: '',
      adminPassword: '',
      reactivateBeneficiaries: true,
      resumePendingClaims: true,
      notifyMember: true,
      confirmReactivation: false,
      conditions: {
        paymentResolved: false,
        documentationComplete: false,
        eligibilityConfirmed: false,
        supervisorApproval: false
      },
      processing: false,
      showValidation: false,
      charCount: 0,
      minReasonLength: 30,
      errorMessage: '',
      successMessage: ''
    };
  },
  computed: {
    memberFullName() {
      if (!this.memberInfo) return 'Unknown Member';

      // Handle different possible property formats
      const firstName = this.memberInfo.first_name || this.memberInfo.firstName || '';
      const middleName = this.memberInfo.middle_name || this.memberInfo.middleName || '';
      const surname = this.memberInfo.surname || this.memberInfo.lastName || '';

      return `${firstName} ${middleName} ${surname}`.trim();
    },
    isReasonValid() {
      return this.reactivationReason.trim().length >= this.minReasonLength;
    },
    charCountClass() {
      if (this.charCount >= this.minReasonLength) {
        return 'text-success';
      } else if (this.charCount > this.minReasonLength * 0.6) {
        return 'text-warning';
      }
      return 'text-danger';
    },
    allConditionsMet() {
      return Object.values(this.conditions).every(condition => condition === true);
    }
  },
  methods: {
    openModal() {
      this.resetForm();
      const modal = new bootstrap.Modal(document.getElementById('reactivateMemberModal'));
      modal.show();
    },

    closeModal() {
      if (this.processing) return;

      const modal = bootstrap.Modal.getInstance(document.getElementById('reactivateMemberModal'));
      if (modal) {
        modal.hide();
      }
      this.resetForm();
    },

    resetForm() {
      this.reactivationReason = '';
      this.adminPassword = '';
      this.reactivateBeneficiaries = true;
      this.resumePendingClaims = true;
      this.notifyMember = true;
      this.confirmReactivation = false;
      this.conditions = {
        paymentResolved: false,
        documentationComplete: false,
        eligibilityConfirmed: false,
        supervisorApproval: false
      };
      this.processing = false;
      this.showValidation = false;
      this.charCount = 0;
      this.errorMessage = '';
      this.successMessage = '';
    },

    updateCharCount() {
      this.charCount = this.reactivationReason.trim().length;
    },

    capitalizeFirst(str) {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1);
    },

    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      return d.toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    },

    formatSuspensionCategory(category) {
      if (!category) return 'Unknown';

      const categoryMap = {
        'non_payment': 'Non-Payment of Premiums',
        'fraud': 'Suspected Fraud',
        'deceased': 'Member Deceased',
        'relocated': 'Member Relocated',
        'duplicate': 'Duplicate Registration',
        'request': 'Member Request',
        'eligibility': 'No Longer Eligible',
        'violation': 'Policy Violation',
        'administrative': 'Administrative Action',
        'other': 'Other Reason'
      };

      return categoryMap[category] || category;
    },

    showError(message) {
      this.errorMessage = message;
      this.successMessage = '';

      // Auto-scroll to top of modal to show error
      const modalBody = document.querySelector('#reactivateMemberModal .modal-body');
      if (modalBody) {
        modalBody.scrollTop = 0;
      }
    },

    showSuccess(message) {
      this.successMessage = message;
      this.errorMessage = '';

      // Auto-scroll to top of modal to show success
      const modalBody = document.querySelector('#reactivateMemberModal .modal-body');
      if (modalBody) {
        modalBody.scrollTop = 0;
      }
    },

    clearError() {
      this.errorMessage = '';
    },

    clearSuccess() {
      this.successMessage = '';
    },

    validateAndSubmit() {
      this.showValidation = true;
      this.clearError();
      this.clearSuccess();

      // Validation
      if (!this.isReasonValid) {
        this.showError(`Please provide a detailed reason (minimum ${this.minReasonLength} characters)`);
        this.$emit('reactivation-error', {
          message: `Please provide a detailed reason (minimum ${this.minReasonLength} characters)`
        });
        return;
      }

      if (!this.adminPassword) {
        this.showError('Please enter your password for authorization');
        this.$emit('reactivation-error', {
          message: 'Please enter your password for authorization'
        });
        return;
      }

      if (!this.allConditionsMet) {
        this.showError('Please confirm all conditions have been met');
        this.$emit('reactivation-error', {
          message: 'Please confirm all conditions have been met'
        });
        return;
      }

      if (!this.confirmReactivation) {
        this.showError('Please confirm the reactivation by checking the confirmation box');
        this.$emit('reactivation-error', {
          message: 'Please confirm the reactivation by checking the confirmation box'
        });
        return;
      }

      // Show confirmation dialog
      const confirmMessage = `Are you sure you want to reactivate ${this.memberFullName}? This will restore the member and all ${this.beneficiaryCount} beneficiaries to active status.`;

      if (confirm(confirmMessage)) {
        this.submitReactivation();
      }
    },

    async submitReactivation() {
      this.processing = true;
      this.clearError();
      this.clearSuccess();

      // Using cbhiNumber instead of memberId
      const data = {
        function: "reactivateMember",
        cbhiNumber: this.cbhiNumber,  // Changed from memberId
        reactivationReason: this.reactivationReason.trim(),
        adminPassword: this.adminPassword,
        reactivateBeneficiaries: this.reactivateBeneficiaries,
        resumePendingClaims: this.resumePendingClaims,
        notifyMember: this.notifyMember
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data && res.data.success) {
          this.showSuccess(res.data.message || 'Member reactivated successfully');

          this.$emit('member-reactivated', {
            success: true,
            message: res.data.message || 'Member reactivated successfully',
            data: res.data.data
          });

          // Close modal after successful reactivation with a delay to show success message
          setTimeout(() => {
            const modalElement = document.getElementById('reactivateMemberModal');
            const modal = bootstrap.Modal.getInstance(modalElement);
            if (modal) {
              modal.hide();
            }

            // Reset form after closing
            setTimeout(() => {
              this.resetForm();
            }, 300);
          }, 2000);
        } else {
          const errorMsg = (res.data && res.data.message) || 'Failed to reactivate member';
          this.showError(errorMsg);
          this.$emit('reactivation-error', {
            message: errorMsg
          });
          this.processing = false;
        }
      } catch (error) {
        console.error('Error reactivating member:', error);
        const errorMsg = error.message || 'An error occurred while reactivating the member';
        this.showError(errorMsg);
        this.$emit('reactivation-error', {
          message: errorMsg
        });
        this.processing = false;
      }
    }
  }
};
</script>

<style scoped>
.modal-header.bg-success {
  background-color: #28a745 !important;
}

.btn-close-white {
  filter: invert(1);
}

.form-check-input:checked {
  background-color: #28a745;
  border-color: #28a745;
}

.cursor-pointer {
  cursor: pointer;
}

.invalid-feedback {
  display: block;
}

textarea {
  resize: vertical;
  min-height: 80px;
}

.alert-success {
  background-color: #d4edda;
  border-color: #28a745;
  color: #155724;
}

.alert-danger {
  background-color: #f8d7da;
  border-color: #dc3545;
  color: #721c24;
}

.card.bg-light {
  background-color: #f8f9fa !important;
}

.badge.bg-warning {
  background-color: #ffc107 !important;
  color: #000;
}

/* Ensure alerts are visible */
.alert {
  position: relative;
  z-index: 1;
}

/* Smooth scroll behavior for modal body */
.modal-body {
  scroll-behavior: smooth;
}
</style>